import { AppLayout } from "@/components/layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useGetAdminPayroll } from "@workspace/api-client-react";
import { formatCurrency } from "@/lib/utils";
import { DollarSign, Users, TrendingUp } from "lucide-react";

export default function AdminPayroll() {
  const { data: payroll, isLoading } = useGetAdminPayroll(undefined, { query: { retry: false } });

  const totalPayout = (payroll || []).reduce((sum: number, t: any) => sum + (t.trainerPayout || 0), 0);
  const totalSessions = (payroll || []).reduce((sum: number, t: any) => sum + (t.sessionCount || 0), 0);

  return (
    <AppLayout>
      <div className="flex flex-col gap-8 pb-12">
        <div>
          <h1 className="text-4xl font-display font-bold text-white mb-2">Payroll & Payouts</h1>
          <p className="text-muted-foreground">Trainer earnings breakdown</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="glass-panel border-white/5">
            <CardHeader className="pb-2">
              <CardDescription className="text-muted-foreground uppercase tracking-wider font-semibold text-xs">Total Payouts</CardDescription>
              <CardTitle className="text-3xl font-mono text-primary">{formatCurrency(totalPayout)}</CardTitle>
            </CardHeader>
          </Card>
          <Card className="glass-panel border-white/5">
            <CardHeader className="pb-2">
              <CardDescription className="text-muted-foreground uppercase tracking-wider font-semibold text-xs">Total Sessions</CardDescription>
              <CardTitle className="text-3xl font-mono text-white">{totalSessions}</CardTitle>
            </CardHeader>
          </Card>
          <Card className="glass-panel border-white/5">
            <CardHeader className="pb-2">
              <CardDescription className="text-muted-foreground uppercase tracking-wider font-semibold text-xs">Trainers</CardDescription>
              <CardTitle className="text-3xl font-mono text-white">{(payroll || []).length}</CardTitle>
            </CardHeader>
          </Card>
        </div>

        <Card className="glass-panel border-white/5">
          <CardHeader>
            <CardTitle className="text-xl font-display uppercase tracking-wide text-white">Trainer Earnings</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-4">{[1, 2, 3].map((i) => <div key={i} className="h-16 bg-white/5 rounded-lg animate-pulse" />)}</div>
            ) : (payroll || []).length === 0 ? (
              <p className="text-muted-foreground text-center py-8">No trainer earnings data yet</p>
            ) : (
              <div className="space-y-3">
                {(payroll || []).map((trainer: any) => (
                  <div key={trainer.trainerId} className="flex items-center justify-between p-4 rounded-xl hover:bg-white/5 border border-white/5 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-sm font-bold text-primary">
                        {trainer.trainerName?.split(" ").map((n: string) => n[0]).join("") || "?"}
                      </div>
                      <div>
                        <p className="font-bold text-white">{trainer.trainerName}</p>
                        <p className="text-xs text-muted-foreground">{trainer.sessionCount} completed sessions</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-mono font-bold text-primary">{formatCurrency(trainer.trainerPayout || 0)}</p>
                      <p className="text-xs text-muted-foreground">
                        avg {formatCurrency(trainer.sessionCount > 0 ? trainer.trainerPayout / trainer.sessionCount : 0)}/session
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
